import { useEffect, useState } from 'react';
import { catchError, EMPTY, tap } from 'rxjs';

import useSecuredApiV2 from './use-secured-api-v2';
import {
  type CustomerOrderOptionsDto,
  toCustomer,
} from '../dto/order-options/customer-order-options-dto';
import { type ResponseDto } from '../dto/ResponseDto';
import { type Customer } from '../entities';
import { CUSTOMER_OPTIONS_END_POINT_URL } from '../utils/endpoints';
import type ProblemDetailsDto from '../dto/error/problem-details-dto';

export interface CustomerOptionsResult {
  data?: Customer[];
  isLoading: boolean;
  errorMessage?: string;
}

/**
 * Loads the customers the current user can place an order for, with their sites, routes and settings, from the
 * customer controller
 */
const useCustomerOptions = (): CustomerOptionsResult => {
  const api = useSecuredApiV2();
  const [state, setState] = useState<CustomerOptionsResult>({ isLoading: true });

  useEffect(() => {
    const subscription = api
      .get(CUSTOMER_OPTIONS_END_POINT_URL, {}, {})
      .pipe(
        tap((response: ResponseDto<CustomerOrderOptionsDto[]>) => {
          setState({ data: response.data.map(toCustomer), isLoading: false });
        }),
        catchError((error: ProblemDetailsDto) => {
          setState({
            data: [],
            isLoading: false,
            errorMessage: error.detail ?? 'Failed to load customers',
          });
          return EMPTY;
        })
      )
      .subscribe();

    // Ignore the response if the component unmounts before it arrives
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return state;
};

export default useCustomerOptions;
